import { FC, useEffect, useMemo, useState } from 'react';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter as DialogFooterUi,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import MoveLeftIcon from '@/components/icons/MoveLeftIcon';
import { Toaster } from 'sonner';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { useTenantDetailsApi } from '@/features/superAdmin/features/tenants/hooks/useTenantDetailsApi';
import { useTenantDemoSeedApi } from '@/features/superAdmin/features/tenants/hooks/useTenantDemoSeedApi';
import ECreateDetailsCopyItem from '@/components/everridge/ECreateDetailsCopyItem';
import { Button } from '@/components/ui/button';
import {
	CreateTenantRequestDto,
	UpdateTenantRequestDto,
} from '@/features/superAdmin/features/tenants/types/superAdmin-tenants-dto';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	TenantCreateSchema,
	TenantFormData,
	TenantUpdateSchema,
} from '@/features/superAdmin/features/tenants/validators/TenantManageValidator';
import ETextInput from '@/components/everridge/ETextInput';
import ETextAreaInput from '@/components/everridge/ETextAreaInput';
import ECheckboxInput from '@/components/everridge/ECheckboxInput';
import ELogoUploaderForm from '@/components/everridge/ELogoUploaderForm';
import TenantColorPicker from '@/features/superAdmin/features/tenants/components/TenantColorPicker';
import TenantApiKeyField from '@/features/superAdmin/features/tenants/components/TenantApiKeyField';
import {
	FlaskConical,
	Loader2,
	MoreVertical,
	Trash2,
} from 'lucide-react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type Props = {
	id?: string;
	onClose: (state: boolean) => void;
};

export type { TenantFormData };

const TenantManage: FC<Props> = ({ id, onClose }) => {
	const [isOpen, setIsOpen] = useState(true);
	const [savedTenantId, setSavedTenantId] = useState<string | undefined>(id);
	const [demoDialogOpen, setDemoDialogOpen] = useState(false);
	const [demoConfirmName, setDemoConfirmName] = useState('');
	const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
	const [removeConfirmName, setRemoveConfirmName] = useState('');

	const effectiveId = savedTenantId ?? id;
	const isEditing = Boolean(effectiveId);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();

	const {
		tenantDetailsData,
		createTenant,
		updateTenant,
		uploadTenantLogo,
		deleteTenant,
		isDeletingTenant,
		isLoadingTenantDetailsData,
	} = useTenantDetailsApi({ id: effectiveId });

	const { demoSeedAvailable, isLoadingAvailability, seedDemoData, isSeeding } =
		useTenantDemoSeedApi(effectiveId);

	const validationSchema = useMemo(
		() => (isEditing ? TenantUpdateSchema : TenantCreateSchema),
		[isEditing],
	);

	const { control, handleSubmit, reset, setValue } = useForm<TenantFormData>({
		resolver: zodResolver(validationSchema),
		defaultValues: {
			enabled: false,
			allowTenantAdminRentAnalyticsExpenseVisibility: false,
			primaryColor: '#000000',
		},
	});

	useEffect(() => {
		if (tenantDetailsData) {
			reset({
				name: tenantDetailsData.name,
				description: tenantDetailsData.description ?? '',
				apiKey: tenantDetailsData.apiKey,
				enabled: tenantDetailsData.enabled,
				logo: tenantDetailsData.logoUrl ?? undefined,
				primaryColor: tenantDetailsData.primaryColor ?? '#000000',
				allowTenantAdminRentAnalyticsExpenseVisibility:
					tenantDetailsData.allowTenantAdminRentAnalyticsExpenseVisibility,
			});
		}
	}, [tenantDetailsData, reset]);

	useEffect(() => {
		setSavedTenantId(id);
	}, [id]);

	const showDemoSeed =
		AppConfig.features.showTenantDemoSeed &&
		isEditing &&
		demoSeedAvailable &&
		!isLoadingAvailability;

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const tenantNameMatches =
		demoConfirmName.length > 0 &&
		demoConfirmName === tenantDetailsData?.name;

	const removeTenantNameMatches =
		removeConfirmName.length > 0 &&
		removeConfirmName === tenantDetailsData?.name;

	const handleDemoSeedSubmit = async () => {
		if (!effectiveId || !tenantNameMatches) return;
		await seedDemoData({ confirmationTenantName: demoConfirmName });
		setDemoDialogOpen(false);
		setDemoConfirmName('');
	};

	const handleRemoveTenantSubmit = async () => {
		if (!effectiveId || !removeTenantNameMatches) return;
		await deleteTenant();
		setRemoveDialogOpen(false);
		setRemoveConfirmName('');
		handleClose();
	};

	const showDangerZone = isEditing;

	const handleCreateForm = async (form: TenantFormData) => {
		const dto: CreateTenantRequestDto = {
			name: form.name,
			description: form.description || '',
			enabled: form.enabled,
			primaryColor: form.primaryColor || '#000000',
			allowTenantAdminRentAnalyticsExpenseVisibility:
				form.allowTenantAdminRentAnalyticsExpenseVisibility,
		};

		const created = await createTenant(dto);
		setSavedTenantId(created.id);

		if (form.logo instanceof File) {
			await uploadTenantLogo({ tenantId: created.id, file: form.logo });
		}
	};

	const handleUpdateForm = async (form: TenantFormData) => {
		const dto: UpdateTenantRequestDto = {
			name: form.name,
			description: form.description || '',
			apiKey: form.apiKey,
			enabled: form.enabled,
			primaryColor: form.primaryColor || '#000000',
			allowTenantAdminRentAnalyticsExpenseVisibility:
				form.allowTenantAdminRentAnalyticsExpenseVisibility,
		};

		await updateTenant(dto);

		if (form.logo instanceof File && effectiveId) {
			await uploadTenantLogo({ tenantId: effectiveId, file: form.logo });
		}

		handleClose();
	};

	const handleSubmitForm = () => {
		isEditing
			? handleSubmit((form) => handleUpdateForm(form))()
			: handleSubmit((form) => handleCreateForm(form))();
	};

	return (
		<Sheet
			open={isOpen}
			onOpenChange={handleClose}>
			<SheetContent
				className={cn('flex flex-col', isMobile ? 'w-full' : 'w-160')}>
				<SheetHeader>
					<SheetDescription />
					<SheetTitle>
						{!isMobile && (
							<SheetClose
								className={cn(
									'flex items-center justify-center cursor-pointer',
									isMobile &&
										'size-11 border border-popover-100 rounded-full',
								)}>
								<MoveLeftIcon />
							</SheetClose>
						)}
						{!!effectiveId
							? t(
									'main.superAdmin.tenants.components.details.title',
							  )
							: t(
									'main.superAdmin.tenants.components.details.title2',
							  )}
						{isMobile && (
							<SheetClose className='size-11 ml-auto flex items-center justify-center cursor-pointer border border-popover-100 rounded-full'>
								<Cancel_01_Icon />
							</SheetClose>
						)}
					</SheetTitle>
				</SheetHeader>
				<div className='flex-1 flex flex-col bg-popover-30 overflow-hidden select-none'>
					<div
						className={cn(
							'h-full p-6 flex flex-col gap-6 overflow-y-scroll no-scrollbar',
							isMobile && 'p-4',
						)}>
						{isEditing && (
							<div
								className={
									'py-10 px-6 flex flex-col items-center gap-4 rounded-2.5xl bg-popover-10 border border-popover-100'
								}>
								<div className='flex flex-col items-center justify-center gap-4 text-center'>
									<h3
										className={cn(
											'text-lg font-medium',
											isMobile && 'text-base',
										)}>
										{tenantDetailsData?.name}
									</h3>
									<ECreateDetailsCopyItem
										type='ID'
										className={cn(
											isMobile && 'justify-between',
										)}>
										{effectiveId}
									</ECreateDetailsCopyItem>
								</div>
							</div>
						)}
						<div className='flex flex-col gap-2'>
							<h3 className={cn(isMobile && 'text-base')}>
								{t(
									'main.superAdmin.tenants.components.details.list.title',
								)}
							</h3>
							<div
								className={cn(
									'p-6 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10',
									isMobile && 'p-0 border-0 bg-popover-30',
								)}>
								<ETextInput
									control={control}
									fieldName='name'
									label={t(
										'main.superAdmin.tenants.form.name.title',
									)}
									placeholder={t(
										'main.superAdmin.tenants.form.name.placeholder',
									)}
								/>
								{isEditing && (
									<TenantApiKeyField
										control={control}
										setValue={setValue}
									/>
								)}
								<ETextAreaInput
									control={control}
									fieldName='description'
									label={t(
										'main.superAdmin.tenants.form.description.title',
									)}
									placeholder={t(
										'main.superAdmin.tenants.form.description.placeholder',
									)}
								/>
								<div className='flex gap-4'>
									<ELogoUploaderForm
										control={control}
										fieldName={'logo'}
										label={t('elements.form.logo.label')}
										placeholder={t(
											'elements.form.logo.placeholder',
										)}
									/>
									<TenantColorPicker
										control={control}
										fieldName={'primaryColor'}
										label={t('elements.form.color.label')}
									/>
								</div>
								<ECheckboxInput
									control={control}
									fieldName='enabled'
									text={t(
										'main.superAdmin.tenants.form.access.title',
									)}
								/>
								<ECheckboxInput
									control={control}
									fieldName='allowTenantAdminRentAnalyticsExpenseVisibility'
									text={t(
										'main.superAdmin.tenants.form.rentAnalyticsExpenseVisibility.title',
									)}
								/>
								<p className='text-xs text-muted-foreground -mt-2'>
									{t(
										'main.superAdmin.tenants.form.rentAnalyticsExpenseVisibility.description',
									)}
								</p>
							</div>
						</div>
					</div>
				</div>
				<SheetFooter className='flex flex-col gap-3'>
					<div className='flex items-center justify-between gap-2'>
						<Button onClick={handleSubmitForm}>
							{isEditing
								? t(
										'main.superAdmin.tenants.components.details.buttons.update',
								  )
								: t(
										'main.superAdmin.tenants.components.details.buttons.create',
								  )}
						</Button>
						{showDangerZone && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										type='button'
										variant='outline'
										size='icon'
										aria-label={t(
											'main.superAdmin.tenants.components.details.moreActions',
										)}
										disabled={isSeeding || isDeletingTenant}>
										{isSeeding || isDeletingTenant ? (
											<Loader2 className='size-4 animate-spin' />
										) : (
											<MoreVertical className='size-4' />
										)}
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align='end' className='min-w-56'>
									<DropdownMenuLabel className='text-xs uppercase tracking-wide text-muted-foreground'>
										{t(
											'main.superAdmin.tenants.components.details.dangerZone.title',
										)}
									</DropdownMenuLabel>
									<DropdownMenuSeparator />
									{showDemoSeed && (
										<DropdownMenuItem
											className='text-destructive focus:text-destructive'
											onSelect={(e) => {
												e.preventDefault();
												setDemoDialogOpen(true);
											}}>
											<FlaskConical className='size-4' />
											{t(
												'main.superAdmin.tenants.components.details.demoSeed.button',
											)}
										</DropdownMenuItem>
									)}
									<DropdownMenuItem
										className='text-destructive focus:text-destructive'
										onSelect={(e) => {
											e.preventDefault();
											setRemoveDialogOpen(true);
										}}>
										<Trash2 className='size-4' />
										{t(
											'main.superAdmin.tenants.components.details.removeTenant.button',
										)}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
					{showDemoSeed && (
						<Dialog
							open={demoDialogOpen}
							onOpenChange={(open) => {
								if (isSeeding && !open) return;
								setDemoDialogOpen(open);
								if (!open) setDemoConfirmName('');
							}}>
							<DialogContent
								className={cn(
									isMobile && 'max-h-[90vh] overflow-y-auto',
								)}
								onPointerDownOutside={(e) => {
									if (isSeeding) e.preventDefault();
								}}
								onEscapeKeyDown={(e) => {
									if (isSeeding) e.preventDefault();
								}}>
								<div className='relative flex min-h-0 flex-1 flex-col gap-6'>
									{isSeeding && (
										<div
											className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2.5xl bg-popover-10/95 p-6'
											aria-busy='true'
											aria-live='polite'>
											<Loader2 className='size-10 animate-spin text-popover-900' />
											<p className='text-center text-sm text-popover-800'>
												{t(
													'main.superAdmin.tenants.components.details.demoSeed.seedingInProgressDetail',
												)}
											</p>
										</div>
									)}
									<DialogHeader>
										<DialogTitle>
											{t(
												'main.superAdmin.tenants.components.details.demoSeed.dialogTitle',
											)}
										</DialogTitle>
										<DialogDescription>
											{t(
												'main.superAdmin.tenants.components.details.demoSeed.dialogDescription',
											)}
										</DialogDescription>
									</DialogHeader>
									<div className='flex flex-col gap-2 py-2'>
										<label
											className='text-sm font-medium text-popover-900'
											htmlFor='demo-seed-tenant-name'>
											{t(
												'main.superAdmin.tenants.components.details.demoSeed.confirmLabel',
											)}
										</label>
										<Input
											id='demo-seed-tenant-name'
											value={demoConfirmName}
											disabled={isSeeding}
											onChange={(e) => setDemoConfirmName(e.target.value)}
											placeholder={t(
												'main.superAdmin.tenants.components.details.demoSeed.confirmPlaceholder',
											)}
											autoComplete='off'
										/>
										{demoConfirmName.length > 0 && !tenantNameMatches && (
											<p className='text-sm text-destructive'>
												{t(
													'main.superAdmin.tenants.components.details.demoSeed.nameMismatch',
												)}
											</p>
										)}
									</div>
									<DialogFooterUi className='flex-col sm:flex-row gap-2 sm:justify-end'>
										<Button
											type='button'
											variant='outline'
											disabled={isSeeding}
											onClick={() => setDemoDialogOpen(false)}>
											{t(
												'main.superAdmin.tenants.components.details.demoSeed.cancel',
											)}
										</Button>
										<Button
											type='button'
											variant='destructive'
											disabled={!tenantNameMatches || isSeeding}
											onClick={() => void handleDemoSeedSubmit()}>
											{isSeeding ? (
												<>
													<Loader2 className='mr-2 size-4 animate-spin' />
													{t(
														'main.superAdmin.tenants.components.details.demoSeed.seedingInProgress',
													)}
												</>
											) : (
												t(
													'main.superAdmin.tenants.components.details.demoSeed.submit',
												)
											)}
										</Button>
									</DialogFooterUi>
								</div>
							</DialogContent>
						</Dialog>
					)}
					{showDangerZone && (
						<Dialog
							open={removeDialogOpen}
							onOpenChange={(open) => {
								if (isDeletingTenant && !open) return;
								setRemoveDialogOpen(open);
								if (!open) setRemoveConfirmName('');
							}}>
							<DialogContent
								className={cn(
									isMobile && 'max-h-[90vh] overflow-y-auto',
								)}
								onPointerDownOutside={(e) => {
									if (isDeletingTenant) e.preventDefault();
								}}
								onEscapeKeyDown={(e) => {
									if (isDeletingTenant) e.preventDefault();
								}}>
								<div className='relative flex min-h-0 flex-1 flex-col gap-6'>
									{isDeletingTenant && (
										<div
											className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2.5xl bg-popover-10/95 p-6'
											aria-busy='true'
											aria-live='polite'>
											<Loader2 className='size-10 animate-spin text-popover-900' />
											<p className='text-center text-sm text-popover-800'>
												{t(
													'main.superAdmin.tenants.components.details.removeTenant.removing',
												)}
											</p>
										</div>
									)}
									<DialogHeader>
										<DialogTitle>
											{t(
												'main.superAdmin.tenants.components.details.removeTenant.dialogTitle',
											)}
										</DialogTitle>
										<DialogDescription>
											{t(
												'main.superAdmin.tenants.components.details.removeTenant.dialogDescription',
											)}
										</DialogDescription>
									</DialogHeader>
									<div className='flex flex-col gap-2 py-2'>
										<label
											className='text-sm font-medium text-popover-900'
											htmlFor='remove-tenant-name'>
											{t(
												'main.superAdmin.tenants.components.details.removeTenant.confirmLabel',
											)}
										</label>
										<Input
											id='remove-tenant-name'
											value={removeConfirmName}
											disabled={isDeletingTenant}
											onChange={(e) => setRemoveConfirmName(e.target.value)}
											placeholder={t(
												'main.superAdmin.tenants.components.details.removeTenant.confirmPlaceholder',
											)}
											autoComplete='off'
										/>
										{removeConfirmName.length > 0 && !removeTenantNameMatches && (
											<p className='text-sm text-destructive'>
												{t(
													'main.superAdmin.tenants.components.details.removeTenant.nameMismatch',
												)}
											</p>
										)}
									</div>
									<DialogFooterUi className='flex-col sm:flex-row gap-2 sm:justify-end'>
										<Button
											type='button'
											variant='outline'
											disabled={isDeletingTenant}
											onClick={() => setRemoveDialogOpen(false)}>
											{t(
												'main.superAdmin.tenants.components.details.removeTenant.cancel',
											)}
										</Button>
										<Button
											type='button'
											variant='destructive'
											disabled={!removeTenantNameMatches || isDeletingTenant}
											onClick={() => void handleRemoveTenantSubmit()}>
											{isDeletingTenant ? (
												<>
													<Loader2 className='mr-2 size-4 animate-spin' />
													{t(
														'main.superAdmin.tenants.components.details.removeTenant.removing',
													)}
												</>
											) : (
												t(
													'main.superAdmin.tenants.components.details.removeTenant.submit',
												)
											)}
										</Button>
									</DialogFooterUi>
								</div>
							</DialogContent>
						</Dialog>
					)}
				</SheetFooter>
				<Toaster />
			</SheetContent>
		</Sheet>
	);
};

export default TenantManage;
